import React, { useState } from 'react';
import { Accordion } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Table from 'react-bootstrap/Table';
import PlaylistAdd from './PlaylistAdd';
import PlaylistEditor from './PlaylistEditor';

type Playlist = {
  id: string;
  level: number;
  progressToNextLevel: number;
  totalXP?: number;
  currentMonthXP?: number;
  accuracy?: number;
  highestScore?: number;
  averageScore?: number;
  roundPlayedCount?: number;
  contentDiscoveredCount?: number;
  totalContentItemsCount?: number;
  contentPack: { id: string; name: string; picture?: string | null };
};

type Props = {
  recentPlaylists?: Playlist[];
  onChangeRecent?: (playlist: Playlist) => void;
  onSaveRecent?: (playlist: Playlist) => void;
  searchedPlaylist?: Playlist | null;
  onChangeSearched: (playlist: Playlist) => void;
  onSaveSearched: (playlist: Playlist) => void;
  onSetCurrentSearched: (playlist: Playlist) => void;
  onSearch: (contentPackId: string) => void;
  onAdd: (contentPackId: string) => void;
  updateMessage?: string | null;
};

const renderTableHeader = () => (
  <thead>
    <tr>
      <th className="text-center">Type</th>
      <th className="text-center">Playlist</th>
      <th className="text-center">Level</th>
      <th className="text-center">Progress</th>
      <th className="text-center">Total XP</th>
      <th className="text-center">Month XP</th>
      <th className="text-center">Rounds</th>
      <th className="text-center">Accuracy</th>
      <th className="text-center">Avg</th>
      <th className="text-center">Best</th>
      <th className="text-center">Songs</th>
      <th className="text-center" />
    </tr>
  </thead>
);

export default function UserPlaylistsDisplay({
  recentPlaylists,
  onChangeRecent,
  onSaveRecent,
  searchedPlaylist,
  onChangeSearched,
  onSaveSearched,
  onSetCurrentSearched,
  onSearch,
  onAdd,
  updateMessage,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [searchContentPackId, setSearchContentPackId] = useState('');
  const recentList = recentPlaylists ?? [];
  const submitSearch = () => {
    const trimmed = searchContentPackId.trim();
    if (!trimmed) return;
    onSearch(trimmed);
  };

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Content Packs</span>
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <div>
              <p className="text-muted small mb-2">
                <i>
                  To view stats for a playlist or artist that is not visible here, search with the content pack ID.
                </i>
              </p>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <InputGroup size="sm" style={{ width: 360 }}>
                  <Form.Control
                    placeholder="Search content pack ID"
                    value={searchContentPackId}
                    onChange={(e) => setSearchContentPackId(e.target.value)}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        submitSearch();
                      }
                    }}
                  />
                  <Button variant="outline-primary" onClick={submitSearch} disabled={!searchContentPackId.trim()}>
                    Search
                  </Button>
                </InputGroup>
                <PlaylistAdd onAdd={onAdd} />
              </div>
              {updateMessage && (
                <Alert variant="success" className="py-1 mb-2">
                  {updateMessage}
                </Alert>
              )}

              {searchedPlaylist && (
                <div className="mb-3">
                  <h6 className="mb-2">Search Result</h6>
                  <div className="table-responsive">
                    <Table bordered hover size="sm" className="mb-0">
                      {renderTableHeader()}
                      <tbody>
                        <PlaylistEditor
                          key={`search-${searchedPlaylist.contentPack.id}`}
                          playlist={searchedPlaylist}
                          typeLabel="Search Result"
                          onChange={onChangeSearched}
                          onSave={onSaveSearched}
                          onSetCurrent={onSetCurrentSearched}
                        />
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              <h6 className="mb-1">Recent UserContentPack</h6>
              <p className="text-muted small mb-2">
                <i>Latest 5 playlist or artist progress rows sorted by last selected time.</i>
              </p>
              {recentList.length > 0 ? (
                <div className="table-responsive mb-3">
                  <Table bordered hover size="sm" className="mb-0">
                    {renderTableHeader()}
                    <tbody>
                      {recentList.map((playlist) => (
                        <PlaylistEditor
                          key={`recent-${playlist.contentPack.id}`}
                          playlist={playlist}
                          typeLabel="Recent"
                          onChange={onChangeRecent}
                          onSave={onSaveRecent}
                        />
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted mb-3">
                  <i>No recent content pack progress</i>
                </p>
              )}
            </div>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
}
